import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function MinimalTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full font-light"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="text-center border-b border-slate-200 py-10 mb-8" style={{ textAlign: s.headerAlignment }}>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border border-slate-300" />}
        <h1 className="font-extralight text-slate-900 tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${3 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        <div className="mt-4 text-sm text-slate-500 tracking-wide">
          {personal.email}{personal.email && personal.phone && '  ·  '}{personal.phone}
          {(personal.email || personal.phone) && personal.location && '  ·  '}{personal.location}
          {(personal.email || personal.phone || personal.location) && personal.linkedin && '  ·  '}{personal.linkedin}
        </div>
      </header>

      <main className="max-w-3xl mx-auto" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center" style={{ fontSize: `${0.75 * hScale}rem` }}>Work Experience</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-normal text-slate-900" style={{ fontSize: `${1.125 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-400 font-light" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-600 mt-1 italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && `, ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline pb-4 border-b border-slate-100">
                  <div>
                    <h3 className="font-normal text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <p className="text-slate-600 italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  </div>
                  <span className="text-slate-400" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {skills.map((skill) => <span key={skill.id} className="text-slate-600" style={{ fontSize: `${0.875 * bScale}rem` }}>{skill.name}</span>)}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center" style={{ fontSize: `${0.75 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <div key={proj.id} className="text-center">
                  <h3 className="font-normal text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                  <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="text-slate-400 mt-2 italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{proj.technologies.join(' · ')}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
            <div className="text-center text-slate-600">
              {certifications.map((cert, i) => (
                <span key={cert.id} style={{ fontSize: `${0.875 * bScale}rem` }}>
                  {cert.name}{cert.issuer && ` (${cert.issuer})`}
                  {i < certifications.length - 1 && <span className="text-slate-300 mx-2">·</span>}
                </span>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
